import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { COACH_FOLLOWED_EVENT, COACH_GET_STARTED_CLICKED_EVENT } from 'constants/analytics';
import { AuthStatus } from 'constants/auth';
import { CURRENCY_USD } from 'constants/currency';
import { MY_COACH_SETTINGS_PAGE, MY_SETTINGS_PAGE, getCoachPageUrl } from 'constants/pages';
import {
  DEFAULT_BANK_ACCOUNT_DESCRIPTION,
  DEFAULT_BANK_ACCOUNT_TITLE,
  getCoachFollowUrl,
} from 'constants/user';
import {
  CoachAvailabilityStatusesEnum,
  CoachStatusEnum,
  FollowStatusesEnum,
  PaymentFulfillmentChannelsEnum,
  SportsEnum,
  UserContentBetaSubscriptions,
  useGetCoachBookingPageLazyQuery,
  useGetCoachPremiumContentForAnonymousUserLazyQuery,
  useGetCoachPremiumContentLazyQuery,
  useGetCoachUpcomingClinicLessonsByIdLazyQuery,
  useGetPlayerUserContentSubscribeStatusLazyQuery,
  useGetUniqueCoachVenueCourtsLazyQuery,
  useGetUserProfileByUsernameLazyQuery,
  useInsertCoachBookingRequestMutation,
  useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation,
  useUpsertFollowerConnectionMutation,
} from 'types/generated/client';
import { updateCacheGetUserProfileFollowing } from 'gql/cache/updateCacheGetUserProfileFollowing';
import { trackEvent } from 'services/client/analytics';
import {
  getEngagementSourceFromUrl,
  trackCtaClick,
  trackView,
} from 'services/client/analytics/engagement';
import { VenueAddress, formatCoachVenueData } from 'utils/shared/coachBuilder';
import { UploadProvider } from 'context/UploadVideoContext';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useMobileView } from 'hooks/useMobileView';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Cog from 'svg/Cog';
import Pencil from 'svg/Pencil';
import Share from 'svg/Share';
import SafeAreaPage from 'layouts/SafeAreaPage';
import { Button, ButtonLink, ButtonText } from 'components/Button';
import Footer from 'components/Footer';
import SetPriceModal from 'components/Premium/SetPriceModal';
import UploadContentModal from 'components/Premium/UploadContentModal';
import { getPlayerSubscribedUntil } from 'components/Premium/utils';
import Switch from 'components/forms/Switch';
import Modal, { useModal } from 'components/modals/Modal';
import ModalShare from 'components/modals/ModalShare';
import ModalShareProfile from 'components/modals/ModalShareProfile';
import ModalStripeOnboarding from 'components/modals/ModalStripeOnboarding';
import Head from 'components/utilities/Head';
import PopppinsFont from 'components/utilities/PoppinsFont';
import classNames from 'styles/utils/classNames';
import BankAccountPrompt from './BankAccountPrompt';
import CoachContent from './CoachContent';
import LessonScheduler from './LessonScheduler';
import ManageFollowCoachModal from './ManageFollowCoachModal';
import ModalCoachPending from './ModalCoachPending';
import RemoteLessonBooking from './RemoteLessonScheduler/RemoteLessonBooking';
import SubscriptionModal from './SubscriptionModal';
import ClinicDisplay from './components/ClinicDisplay';
import CompleteProfile from './components/CompleteProfile';
import ContactCoach from './components/ContactCoach';
import ContactCoachSuccessModal from './components/ContactCoachSuccessModal';
import ExternalBookingMsg from './components/ExternalBookingMsg';
import TabSwitch, { TabsEnum } from './components/TabSwitch';
import { UPCOMING_GROUP_LESSON_LOAD_COUNT } from './constants';
import { PageProps, Steps } from './types';
import { getShouldShowContactCoach } from './utils';

const FOLLOW_QUERY_PARAM = '1';
const UNAVAILABLE_COACH_TITLE = 'Book now';
const UNAVAILABLE_COACH_DESCRIPTION =
  'See available times and book a lesson with this coach on Bounce.';

const PlayerRequestBooking = ({ action }: { action: () => void }) => {
  return (
    <div>
      <p className="text-pretty text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
        {UNAVAILABLE_COACH_DESCRIPTION}
      </p>
      <div className="mt-6">
        <Button onClick={() => action()} variant="brand" size="lg">
          View schedule
        </Button>
      </div>
    </div>
  );
};

export default function CoachDetails({
  coach,
  coachStarterProfile,
  usernameOrId,
  jsonLd,
}: PageProps) {
  if (!coach && !coachStarterProfile) {
    return null;
  }

  const uploadContentModal = useModal();
  const setPriceModal = useModal();

  const timeout = useRef<ReturnType<typeof setTimeout> | null>();

  const [isFollowRequestPending, setIsFollowRequestPending] = useState(false);

  const [
    getUserProfileByUsernameQuery,
    { data: userProfileData, loading: userProfileDataLoading },
  ] = useGetUserProfileByUsernameLazyQuery();

  const [
    getCoachPremiumContent,
    { data: premiumCoachContentForUser, loading: premiumCoachContentForUserLoading },
  ] = useGetCoachPremiumContentLazyQuery();

  const [
    getCoachPremiumContentForAnonymousUser,
    { data: premiumCoachContentForAnonymous, loading: premiumCoachContentForAnonymousLoading },
  ] = useGetCoachPremiumContentForAnonymousUserLazyQuery();

  const [getPlayerUserContentSubscribeStatus, { data: playerUserContentSubscribeStatus }] =
    useGetPlayerUserContentSubscribeStatusLazyQuery();

  const followingDetails = userProfileData?.userFollows?.[0];

  const premiumCoachContent = premiumCoachContentForUser || premiumCoachContentForAnonymous;

  const premiumContentLoading =
    premiumCoachContentForUserLoading || premiumCoachContentForAnonymousLoading;

  const subscriptionPrice =
    premiumCoachContent?.userProfiles?.[0]?.priceUnitAmountPremiumContentDefault || 0;

  const description = premiumCoachContent?.userProfiles[0].premiumContentDescription || '';

  const priceFormInitialValues = useMemo(
    () => ({
      price: subscriptionPrice,
      description,
    }),
    [premiumCoachContent],
  );

  const shareAction = () => {
    openShareModal();
    if (navigator.share) {
      navigator
        .share({
          title: `${coach.fullName || coachStarterProfile?.name} - book lessons on Bounce`,
          text: `More details at ${shareUrl}`,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  const [didOpenFollowPrompt, setDidOpenFollowPrompt] = useState(false);

  const { openSignupModal, closeLoginModal, closeSignupModal, ModalLogin, ModalSignup } =
    useAuthModals();
  const {
    isOpen: isOpenCoachPending,
    openModal: openModalCoachPending,
    closeModal: closeModalCoachPending,
  } = useModal();
  const [insertCoachBookingRequestMutation] = useInsertCoachBookingRequestMutation();

  const {
    isOpen: isShareModalOpen,
    openModal: openShareModal,
    closeModal: closeShareModal,
  } = useModal();

  const {
    isOpen: isContactCoachModalOpen,
    openModal: openContactCoachModal,
    closeModal: closeContactCoachModal,
  } = useModal();

  const {
    isOpen: isContactCoachSuccessModalOpen,
    openModal: openContactCoachSuccessModal,
    closeModal: closeContactCoachSuccessModal,
  } = useModal();

  const handleContactCoachSuccess = () => {
    // Engagement: a submitted contact/booking request is a "player inquiry" for
    // this coach (Coach Insights on the club dashboard reads COACH_PROFILE clicks).
    if (coachId) {
      trackCtaClick({
        entityType: 'COACH_PROFILE',
        entityId: coachId,
        source: getEngagementSourceFromUrl(),
      });
    }
    closeContactCoachModal();
    openContactCoachSuccessModal();
  };

  const {
    isOpen: isCoachFollowAlertModalOpen,
    openModal: openCoachFollowAlertModal,
    closeModal: closeCoachFollowAlertModal,
  } = useModal();
  const {
    isOpen: isUnfollowModalOpen,
    openModal: openUnfollowModal,
    closeModal: closeUnfollowModal,
  } = useModal();

  const {
    isOpen: isBookLessonModalOpen,
    openModal: openBookLessonModal,
    closeModal: closeBookLessonModal,
  } = useModal();

  const {
    isOpen: isBookRemoteLessonModalOpen,
    openModal: openBookRemoteLessonModal,
    closeModal: closeBookRemoteLessonModal,
  } = useModal();

  const {
    isOpen: isBankAccountConnectedModalOpen,
    openModal: openBankAccountConnectedModal,
    closeModal: closeBankAccountConnectedModal,
  } = useModal();

  const {
    openModal: openShareProfileModal,
    closeModal: closeShareProfileModal,
    isOpen: isShareProfileModalOpen,
  } = useModal();

  const {
    openModal: openSubscriptionModal,
    closeModal: closeSubscriptionModal,
    isOpen: isSubscriptionModalOpen,
  } = useModal();

  const {
    openModal: openBookGroupClinicModal,
    closeModal: closeBookGroupClinicModal,
    isOpen: isBookGroupClinicModalOpen,
  } = useModal();

  const shareUrl =
    typeof window !== 'undefined' && coach
      ? getCoachFollowUrl(coach?.username?.toLowerCase() || coach?.id || '')
      : '';

  const [venueFromMap, setVenueFromMap] = useState<VenueAddress | null>(null);

  const viewer = useViewer();

  const { user } = useGetCurrentUser();
  const router = useRouter();

  const isCoach = user?.coachStatus === CoachStatusEnum.Active;
  const userId = user?.id;
  const followingCoachesCount = user?.followingCoachesAggregate?.aggregate?.count;
  const coachId = coach?.id;
  const isRouterFollow = router.query.follow === FOLLOW_QUERY_PARAM;
  const isCoachOfPage = userId === coachId;
  const isMobile = useMobileView();

  // Engagement: record a coach-profile view (coach.id is the coach's user id,
  // which is what owner_user_id / venue_staff_coaches match on).
  useEffect(() => {
    if (coachId) {
      trackView({
        entityType: 'COACH_PROFILE',
        entityId: coachId,
        source: getEngagementSourceFromUrl(),
      });
    }
  }, [coachId]);
  const needBankInformation = !!user && !user.stripeMerchantChargesEnabled;
  const didSkipStripe = user?.didSkipStripeSetup;
  const isSubscriptionActionPending = useRef<boolean>(false);

  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [paymentFulfillmentChannel, setPaymentFulfillmentChannel] = useState(
    PaymentFulfillmentChannelsEnum.OnPlatform,
  );
  const [updateUserDefaultCoachPaymentFulfillmentChannel] =
    useUpdateUserDefaultCoachPaymentFulfillmentChannelMutation();

  /**
   * @note (trey): I know said we should prefer normal queries over lazy queries, but in this case the startDateTime is dynamic, and when it changes, it was causing a refetch. The useEffect allows us to run it once.
   */
  const [getCoachBookingPageLazyQuery, { data: coachUpdatedData }] =
    useGetCoachBookingPageLazyQuery();

  const [getUniqueCoachVenueCourts] = useGetUniqueCoachVenueCourtsLazyQuery();

  const [
    getCoachUpcomingClinicLessonsById,
    { loading: coachUpcomingClinicLessonsLoading, data: coachUpcomingClinicLessonsData },
  ] = useGetCoachUpcomingClinicLessonsByIdLazyQuery();

  const [upsertFollowerMutation, { loading: changeFollowStatusLoading }] =
    useUpsertFollowerConnectionMutation();

  const [venues, setVenues] = useState<VenueAddress[]>([]);
  const [steps, setSteps] = useState(Steps.VideoUploader);
  const [viewAsClient, setViewAsClient] = useState(false);
  const [accumulatedClinicLessons, setAccumulatedClinicLessons] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState<TabsEnum>(TabsEnum.Court);

  const isFollowingCoach = followingDetails?.status === FollowStatusesEnum.Active;

  const shouldShowContactCoach = useMemo(
    () => !isCoachOfPage && getShouldShowContactCoach(coach),
    [coach, isCoachOfPage],
  );

  const getCoachBookingData = () => {
    getCoachBookingPageLazyQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        id: coach?.id,
        username: coach?.username || coach?.id,
        startDateTime: new Date().toISOString(),
      },
    });
  };

  const handleGetUpcomingLessonsById = useCallback(
    async (currentOffset: number) => {
      try {
        const result = await getCoachUpcomingClinicLessonsById({
          variables: {
            ownerUserId: coach.id,
            limit: UPCOMING_GROUP_LESSON_LOAD_COUNT,
            offset: currentOffset,
          },
          fetchPolicy: 'cache-first',
        });

        if (result.data?.lessons) {
          if (currentOffset === 0) {
            // Initial load - replace all lessons
            setAccumulatedClinicLessons(result.data.lessons);
          } else {
            // Load more - append to existing lessons
            setAccumulatedClinicLessons((prev) => [...prev, ...(result.data?.lessons || [])]);
          }
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [getCoachUpcomingClinicLessonsById, coach.id],
  );

  const handleGetPlayerUserContentSubscribeStatus = useCallback(async () => {
    try {
      await getPlayerUserContentSubscribeStatus({
        variables: {
          coachId,
          playerId: viewer.userId,
        },
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
      });
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [viewer.userId, coachId, getPlayerUserContentSubscribeStatus]);

  React.useEffect(() => {
    if (!router.isReady) return;

    const fetchProfile = async () => {
      try {
        if (viewer.status === AuthStatus.User && viewer.userId) {
          await getUserProfileByUsernameQuery({
            variables: {
              username: usernameOrId.toLowerCase(),
              viewerId: viewer.userId,
            },
          });
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    fetchProfile();
  }, [viewer.userId, viewer.status, router.isReady]);

  useEffect(() => {
    const init = async () => {
      if (coach?.id) {
        getCoachBookingPageLazyQuery({
          fetchPolicy: 'cache-and-network',
          variables: {
            id: coach?.id,
            username: coach?.username || coach?.id,
            startDateTime: new Date().toISOString(),
          },
        });
        const unqiueCoachVenueCourts = await getUniqueCoachVenueCourts({
          fetchPolicy: 'cache-and-network',
          variables: {
            userId: coach.id,
          },
        });
        if (unqiueCoachVenueCourts.data) {
          const venues = formatCoachVenueData(unqiueCoachVenueCourts.data.coachAvailability);
          setVenues(venues);
        }
      }
    };
    init();
  }, [coach?.id]);

  useEffect(() => {
    if (coach?.id) {
      handleGetUpcomingLessonsById(0);
    }
  }, [handleGetUpcomingLessonsById, coach?.id]);

  useEffect(() => {
    if (needBankInformation && !didSkipStripe) {
      openBankAccountConnectedModal();
    }
  }, [needBankInformation, didSkipStripe]);

  const coachData = coachUpdatedData?.userProfiles?.[0] || coach;

  const sportText =
    coachData && coachData.defaultSport
      ? ` | ${
          coachData.defaultSport === SportsEnum.Tennis
            ? 'Tennis'
            : coachData.defaultSport === SportsEnum.Pickleball
            ? 'Pickleball'
            : coachData.defaultSport === SportsEnum.Padel
            ? 'Padel'
            : coachData.defaultSport === SportsEnum.PlatformTennis
            ? 'Platform Tennis'
            : ''
        } Coach`
      : ' | Coach Profile';

  const requestBookingAction = () => {
    if (!viewer.userId) {
      openSignupModal();
    } else {
      openModalCoachPending();
      insertCoachBookingRequestMutation({
        variables: {
          playerUserId: viewer.userId,
          coachUserId: coachData?.id || null,
          coachStarterProfileId: coachStarterProfile?.id || null,
        },
      });
    }
  };

  const handleBookVenue = (venue: VenueAddress) => {
    if (isMobile) {
      openBookLessonModal();
    }
    setActiveTab(TabsEnum.Court);
    setVenueFromMap({ ...venue }); // a new object to trigger the flow everytime
  };

  useEffect(() => {
    if (router.query.share === 'true') {
      openShareProfileModal();
    }
  }, [router.query]);

  const handleGetStarted = () => {
    trackEvent(COACH_GET_STARTED_CLICKED_EVENT, {
      coachId: coachData?.id,
      surface: 'remote_coaching_card',
    });
    if (isMobile) {
      openBookRemoteLessonModal();
    } else {
      setActiveTab(TabsEnum.Remote);
      setSteps(Steps.VideoUploader);
    }
  };

  const handleChangeCoachFollowing = useCallback(
    (follow: boolean) => {
      const isLoggedIn = viewer.userId;
      if (isLoggedIn) {
        const variables = {
          followerUserId: viewer.userId,
          followedUserId: coachData.id,
          status: follow ? FollowStatusesEnum.Active : FollowStatusesEnum.Inactive,
        };
        try {
          upsertFollowerMutation({
            update: updateCacheGetUserProfileFollowing(usernameOrId.toLowerCase() || ''),
            variables: variables,
            refetchQueries: ['checkUserFollowsCoach'],
            optimisticResponse: {
              __typename: 'mutation_root',
              insertUserFollowsOne: {
                __typename: 'UserFollows',
                ...variables,
              },
            },
          });
          if (follow) {
            trackEvent(COACH_FOLLOWED_EVENT, { coachId: coachData.id });
          }
        } catch (error) {
          Sentry.captureException(error);
        }
      } else {
        closeCoachFollowAlertModal(false);
        setTimeout(() => {
          openSignupModal(true);
        }, 1000);
      }
    },
    [viewer.userId, usernameOrId],
  );

  const handleCoachFollowingBtnClick = (follow: boolean) => {
    setDidOpenFollowPrompt(true);

    if (follow) {
      handleChangeCoachFollowing(true);
    } else {
      openUnfollowModal();
    }
  };

  const getCompleteProfile = () => {
    if (viewAsClient) {
      return !!coachData.coachExternalBookingUrl ? (
        <ExternalBookingMsg
          onContactCoach={() => openContactCoachModal()}
          coachExternalBookingUrl={coachData.coachExternalBookingUrl || ''}
        />
      ) : (
        <ContactCoach
          coachData={coachData}
          starterId={coachStarterProfile?.id}
          openAuthModal={openSignupModal}
          visible={true}
          readOnly
          onSuccess={handleContactCoachSuccess}
        />
      );
    }
    return (
      <CompleteProfile
        needBankInformation={needBankInformation}
        hasDisabledAvailability={hasDisabledAvailability}
        onConnectBankAccount={() => {}}
        isCoachOfPage={isCoachOfPage}
      />
    );
  };

  useEffect(() => {
    if (router.isReady && coach && !viewer.isSessionLoading) {
      const { follow, ...restQuery } = router.query;

      if (follow !== FOLLOW_QUERY_PARAM) return;

      if (viewer?.userId && !userProfileData) {
        return;
      }

      if (viewer.userId !== coach.id && !isFollowingCoach && !isBookRemoteLessonModalOpen) {
        setDidOpenFollowPrompt(true);
        openCoachFollowAlertModal();
      }

      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [router.isReady, router.query, viewer, coach, userProfileDataLoading, isFollowingCoach]);

  useEffect(() => {
    if (didOpenFollowPrompt) {
      return;
    }

    if (isRouterFollow) {
      return;
    }

    if (
      viewer.isAnonymousSession ||
      (user && user.coachStatus !== CoachStatusEnum.Active && followingCoachesCount === 0)
    ) {
      timeout.current = setTimeout(() => {
        if (!isBookRemoteLessonModalOpen) {
          setDidOpenFollowPrompt(true);
          openCoachFollowAlertModal();
        }
      }, 3000);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [didOpenFollowPrompt, user, followingCoachesCount, isRouterFollow, viewer.isAnonymousSession]);

  useEffect(() => {
    if (viewer.userId && isFollowRequestPending) {
      if (viewer.userId !== coach.id) {
        handleChangeCoachFollowing(true);
      }
      setIsFollowRequestPending(false);
    }
  }, [viewer.userId, coach?.id, isFollowRequestPending]);

  const handleAuthCompleteForFollowing = () => {
    if (viewer.userId) {
      handleChangeCoachFollowing(true);
      return;
    }
    setIsFollowRequestPending(true);
  };

  const handleRemoteLessonBookingModalClose = () => {
    setTimeout(() => {
      setSteps(Steps.VideoUploader);
    }, 300); // to align with modal close animation
    closeBookRemoteLessonModal();
  };

  const playerSubscribedUntil = useMemo(
    () =>
      viewer.userId
        ? getPlayerSubscribedUntil(
            playerUserContentSubscribeStatus?.userContentBetaSubscriptions as UserContentBetaSubscriptions[],
            viewer.userId,
          )
        : null,
    [playerUserContentSubscribeStatus?.userContentBetaSubscriptions, viewer.userId],
  );

  const handleGetCoachPremiumContent = useCallback(async () => {
    try {
      if (viewer.isSessionLoading) return;
      if (viewer.userId) {
        await getCoachPremiumContent({
          variables: {
            coachId,
          },
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'network-only',
        });
      } else {
        await getCoachPremiumContentForAnonymousUser({
          variables: {
            coachId,
          },
          fetchPolicy: 'network-only',
          nextFetchPolicy: 'network-only',
        });
      }
    } catch (err) {
      Sentry.captureException(err);
    }
  }, [coachId, getCoachPremiumContent, viewer]);

  useEffect(() => {
    if (coachId) {
      handleGetCoachPremiumContent();
    }
  }, [handleGetCoachPremiumContent, coachId]);

  useEffect(() => {
    viewer.userId && coachId && handleGetPlayerUserContentSubscribeStatus();
  }, [viewer.userId, coachId]);

  const hasUpcomingGroupClinics =
    (coachUpcomingClinicLessonsData?.lessonsAggregate?.aggregate?.count || 0) > 0;

  // Create merged clinic data with accumulated lessons
  const mergedClinicData = coachUpcomingClinicLessonsData
    ? {
        ...coachUpcomingClinicLessonsData,
        lessons: accumulatedClinicLessons,
      }
    : null;

  const hasDisabledAvailability = useMemo(() => {
    if (!coach.priceUnitAmountCoachDefault) {
      return true;
    }
    return coach.coachAvailability.every(
      (availability) => availability.status === CoachAvailabilityStatusesEnum.Inactive,
    );
  }, [coach.priceUnitAmountCoachDefault, coach.coachAvailability]);

  return (
    <>
      <Head
        title={`${
          coachData?.fullName || coachData?.username || coachStarterProfile?.name || ''
        }${sportText}`}
        description={`Take lessons from coach ${
          coachData?.fullName || coachData?.username || coachStarterProfile?.name || ''
        }`}
        ogImage={
          coachData?.profileImageProviderUrl ||
          coachStarterProfile?.profileImageUrl ||
          coachStarterProfile?.logoImageUrl ||
          coachStarterProfile?.aboutPhotoImageUrl ||
          undefined
        }
        canonicalUrl={`${process.env.APP_URL}${getCoachPageUrl(
          (coachData?.username || coachData?.id || '').toLowerCase(),
        )}`}
      />
      <PopppinsFont />

      <SafeAreaPage isShowTopNav isHideSidebar>
        {isCoach && isCoachOfPage && (
          <div className="z-20 mx-auto h-16 w-full flex-auto flex-row items-center justify-between border-b bg-color-bg-lightmode-primary px-8 py-4 dark:bg-color-bg-darkmode-primary xs:hidden lg:flex">
            {/* Hiding Breadcrumbs until we have a marketplace or until we add coaches to city pages */}
            <div>
              {/* <Breadcrumbs
                breadcrumbs={
                  generateBreadcrumbs({
                    coachName: coach.fullName as string,
                    coachSlug: coach.username as string,
                  }).ui
                }
              /> */}
            </div>
            <div className="flex items-center justify-center gap-2">
              {isCoach && isCoachOfPage && (
                <Switch
                  className="mr-2"
                  labelRight="View as player"
                  labelRightClassName="ml-2 typography-product-button-label-medium text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                  isActive={viewAsClient}
                  toggleIsActive={() => setViewAsClient(!viewAsClient)}
                />
              )}
              {/* {isCoach && isCoachOfPage && (
                <ButtonLink
                  href={MY_COACH_SETTINGS_PAGE}
                  variant="secondary"
                  size="md"
                  iconLeft={
                    <Pencil className="inline-block h-5 text-color-text-lightmode-primary" />
                  }
                  isInline
                >
                  Edit Listing
                </ButtonLink>
              )} */}
              <ButtonLink
                href={MY_SETTINGS_PAGE}
                iconLeft={<Cog className="inline-block h-5 text-color-text-lightmode-primary" />}
                variant="secondary"
                size="md"
                isInline
                className="xs:w-full"
              >
                Settings
              </ButtonLink>
              <Button
                onClick={shareAction}
                iconLeft={<Share className="inline-block h-5 text-color-text-darkmode-primary" />}
                variant="primary"
                size="md"
                isInline
              >
                Share my listing
              </Button>
            </div>
          </div>
        )}
        <main className="mx-auto h-full w-full max-w-[72.5rem] bg-color-bg-lightmode-primary pb-4 dark:bg-color-bg-darkmode-primary lg:pb-20">
          <div className={classNames('flex h-full w-full lg:flex-row-reverse lg:pt-6')}>
            <div
              className={classNames(
                'sticky top-[5.5rem] hidden h-full w-full self-start bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary lg:block lg:max-w-[26rem]',
              )}
            >
              {isCoach &&
              isCoachOfPage &&
              !!user &&
              (needBankInformation || hasDisabledAvailability) ? (
                getCompleteProfile()
              ) : (
                <>
                  {coachData?.stripeMerchantDetailsSubmitted &&
                    !coachStarterProfile &&
                    venues.length > 0 && (
                      // Remote coaching

                      <>
                        <section className="rounded-xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary shadow-lightmode-primary dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary">
                          {!!coachData?.priceUnitAmountRemoteCoachDefault ? (
                            <TabSwitch
                              venueFromMap={venueFromMap}
                              coachData={coachData}
                              lessons={coachData?.coachLessons}
                              setVenueFromMap={setVenueFromMap}
                              steps={steps}
                              setSteps={setSteps}
                              activeTab={activeTab}
                              setActiveTab={setActiveTab}
                              subscriptionPrice={subscriptionPrice}
                              playerSubscribedUntil={playerSubscribedUntil}
                              onSubscriptionSuccess={handleGetPlayerUserContentSubscribeStatus}
                            />
                          ) : (
                            <LessonScheduler
                              venueFromMap={venueFromMap}
                              coach={coachData}
                              lessons={coachData?.coachLessons}
                              setVenueFromMap={setVenueFromMap}
                            />
                          )}
                        </section>
                      </>
                    )}

                  {!!coachData.coachExternalBookingUrl && shouldShowContactCoach ? (
                    <ExternalBookingMsg
                      onContactCoach={() => openContactCoachModal()}
                      coachExternalBookingUrl={coachData.coachExternalBookingUrl || ''}
                    />
                  ) : (
                    <ContactCoach
                      coachData={coachData}
                      starterId={coachStarterProfile?.id}
                      openAuthModal={openSignupModal}
                      visible={shouldShowContactCoach}
                      onSuccess={handleContactCoachSuccess}
                    />
                  )}
                  {hasUpcomingGroupClinics && (
                    <div className="mt-2 rounded-xl border border-color-border-input-lightmode bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                            Group Clinics
                          </p>
                          <p className="text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                            {coachUpcomingClinicLessonsData?.lessonsAggregate?.aggregate?.count}{' '}
                            clinics to join
                          </p>
                        </div>
                        <Button
                          onClick={() => openBookGroupClinicModal()}
                          variant="secondary"
                          size="sm"
                          isInline
                        >
                          View all
                        </Button>
                      </div>
                    </div>
                  )}
                  {!shouldShowContactCoach && !isCoachOfPage && (
                    <div className="mt-4 px-6">
                      <Button variant="secondary" size="lg" onClick={() => openContactCoachModal()}>
                        Contact coach
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className={classNames('relative w-full min-w-0 flex-1 lg:pr-10')}>
              <CoachContent
                shareUrl={shareUrl}
                getCoachBookingData={getCoachBookingData}
                viewAsClient={viewAsClient}
                isCoachOfPage={isCoachOfPage}
                premiumContentLoading={premiumContentLoading}
                premiumCoachContent={premiumCoachContent || null}
                onEditPremiumPrice={setPriceModal.openModal}
                onUploadContent={uploadContentModal.openModal}
                onSubscribe={() => {
                  if (!isMobile) {
                    setActiveTab(TabsEnum.Content);
                    return;
                  }
                  openSubscriptionModal();
                }}
                coach={coachData}
                venues={venues}
                coachStarterProfile={coachStarterProfile}
                handleBookVenue={handleBookVenue}
                handleGetStarted={handleGetStarted}
                isFollowingCoach={isFollowingCoach}
                hideToggleFollowBtn={
                  (!!viewer?.userId && !coach?.id && viewer?.userId === coach?.id) || !coach?.id
                }
                handleCoachFollowingBtnClick={handleCoachFollowingBtnClick}
                isPlayerSubscribedToPremium={!!playerSubscribedUntil}
                coachUpcomingClinicLessonsData={mergedClinicData}
                coachUpcomingClinicLessonsLoading={coachUpcomingClinicLessonsLoading}
                handleGetUpcomingLessonsById={handleGetUpcomingLessonsById}
              />
            </div>
          </div>
        </main>
        <div className="fixed z-50 flex w-full items-center justify-center p-4  xs:bottom-24 md:bottom-6 lg:hidden">
          <div className="mx-auto flex w-full max-w-lg flex-row gap-4">
            {!isCoachOfPage && (
              <Button
                variant="brand"
                size="lg"
                onClick={() => openContactCoachModal()}
                className="w-full"
              >
                Contact Coach
              </Button>
            )}

            {!shouldShowContactCoach && (
              <Button
                variant="primary"
                size="lg"
                onClick={() => openBookLessonModal()}
                className="w-full"
              >
                Private Lesson
              </Button>
            )}

            {hasUpcomingGroupClinics && (
              <Button
                variant="brand"
                size="lg"
                onClick={() => openBookGroupClinicModal()}
                className="w-full"
              >
                Group Clinics
              </Button>
            )}
            {/* {!!coach?.priceUnitAmountRemoteCoachDefault && (
              <Button
                variant="brand"
                size="lg"
                onClick={() => {
                  timeout?.current && clearTimeout(timeout?.current);
                  openBookRemoteLessonModal();
                }}
                className="w-full"
              >
                Remote
              </Button>
            )} */}
          </div>
        </div>
        <Footer />
      </SafeAreaPage>
      <ModalStripeOnboarding
        isOpen={isPayoutModalOpen}
        handleClose={(didAddBankInformation) => {
          setIsPayoutModalOpen(false);
          if (viewer.userId) {
            if (didAddBankInformation) {
              updateUserDefaultCoachPaymentFulfillmentChannel({
                variables: {
                  id: viewer.userId,
                  defaultCoachPaymentFulfillmentChannel: PaymentFulfillmentChannelsEnum.OnPlatform,
                },
              }).catch((error) => Sentry.captureException(error));
              setPaymentFulfillmentChannel(PaymentFulfillmentChannelsEnum.OnPlatform);
            }
          }
        }}
      />

      <ModalShare
        isOpen={isShareModalOpen}
        closeModal={closeShareModal}
        shareUrl={shareUrl}
        title="Share this link"
      />

      {/* On-court lesson Modal */}
      <Modal isOpen={isBookLessonModalOpen} handleClose={closeBookLessonModal}>
        {steps === Steps.SelectTime && (
          <ButtonText
            onClick={() => closeBookLessonModal()}
            className="absolute right-4 top-4 z-100 rounded-full p-2"
          >
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
        )}
        {/* Show Bank info on mobile screen */}
        {isCoach && isCoachOfPage && !!user && (needBankInformation || hasDisabledAvailability) ? (
          getCompleteProfile()
        ) : (
          <>
            {coachData?.stripeMerchantDetailsSubmitted && venues.length > 0 && (
              <LessonScheduler
                coach={coachData}
                lessons={coachData?.coachLessons}
                venueFromMap={venueFromMap}
                setVenueFromMap={setVenueFromMap}
              />
            )}

            {!!coachData.coachExternalBookingUrl && shouldShowContactCoach ? (
              <ExternalBookingMsg
                onContactCoach={() => openContactCoachModal()}
                coachExternalBookingUrl={coachData.coachExternalBookingUrl || ''}
              />
            ) : (
              <ContactCoach
                coachData={coachData}
                starterId={coachStarterProfile?.id}
                openAuthModal={openSignupModal}
                visible={shouldShowContactCoach}
                onSuccess={handleContactCoachSuccess}
              />
            )}
          </>
        )}
      </Modal>

      {/* Remote coaching Modal */}
      <Modal isOpen={isBookRemoteLessonModalOpen} handleClose={handleRemoteLessonBookingModalClose}>
        {steps === Steps.VideoAnalysis && (
          <>
            <ButtonText
              onClick={() => closeBookRemoteLessonModal()}
              className="absolute right-4 top-4 z-100 rounded-full p-2"
            >
              <CloseIcon className="h-5 w-5" />
            </ButtonText>
          </>
        )}

        {/* Show Bank info on mobile screen */}
        {isCoach && isCoachOfPage && !!user && (needBankInformation || hasDisabledAvailability) ? (
          getCompleteProfile()
        ) : (
          <>
            {coachData?.stripeMerchantDetailsSubmitted && venues.length > 0 && (
              <RemoteLessonBooking
                coach={coach}
                steps={steps}
                setSteps={setSteps}
                handleClose={handleRemoteLessonBookingModalClose}
              />
            )}
          </>
        )}
      </Modal>
      {isCoach && isCoachOfPage && !!user && (needBankInformation || hasDisabledAvailability) && (
        <Modal
          isOpen={isBankAccountConnectedModalOpen}
          handleClose={closeBankAccountConnectedModal}
        >
          <ButtonText
            onClick={() => closeBankAccountConnectedModal()}
            className="absolute right-4 top-4 z-100 rounded-full p-2"
          >
            <CloseIcon className="h-5 w-5" />
          </ButtonText>
          {getCompleteProfile()}
        </Modal>
      )}
      <ModalSignup
        isShowLoginLink
        shouldSkipReload
        handleAuthComplete={() => {
          closeSignupModal();
          handleAuthCompleteForFollowing();
        }}
      />
      <ModalLogin
        isShowSignupLink
        shouldSkipReload
        handleAuthComplete={() => {
          closeLoginModal();
          handleAuthCompleteForFollowing();
        }}
      />
      <ModalCoachPending isOpen={isOpenCoachPending} handleClose={closeModalCoachPending} />
      <ModalShareProfile
        isOpen={isShareProfileModalOpen}
        closeModal={closeShareProfileModal}
        shareUrl={shareUrl}
        title="Share this link"
      />
      <ManageFollowCoachModal
        isFollowPrompt
        loading={changeFollowStatusLoading}
        isOpen={isCoachFollowAlertModalOpen}
        handleClose={closeCoachFollowAlertModal}
        handleChangeCoachFollowing={handleChangeCoachFollowing}
      />
      <ManageFollowCoachModal
        isFollowPrompt={false}
        loading={changeFollowStatusLoading}
        isOpen={isUnfollowModalOpen}
        handleClose={closeUnfollowModal}
        handleChangeCoachFollowing={handleChangeCoachFollowing}
      />

      <SubscriptionModal
        steps={steps}
        isOwner={isCoachOfPage}
        setSteps={setSteps}
        isOpen={isSubscriptionModalOpen}
        handleClose={closeSubscriptionModal}
        coachId={coachId}
        coachName={coachData?.fullName || ''}
        subscriptionPrice={subscriptionPrice}
        playerSubscribedUntil={playerSubscribedUntil}
        onSubscriptionSuccess={handleGetPlayerUserContentSubscribeStatus}
        currency={
          coachData?.stripeMerchantCurrency || coachData?.currencyCoachDefault || CURRENCY_USD
        }
      />

      {/* Group Clinics Modal */}
      <Modal
        isOpen={isBookGroupClinicModalOpen}
        handleClose={closeBookGroupClinicModal}
        classNameMaxWidth="max-w-5xl"
      >
        <ButtonText
          onClick={() => closeBookGroupClinicModal()}
          className="absolute right-4 top-4 z-100 rounded-full p-2"
        >
          <CloseIcon className="h-5 w-5" />
        </ButtonText>
        <div className="flex max-h-[92dvh] flex-col pt-6">
          <h2 className="typography-product-heading-compact mb-4 px-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            Group Clinics
          </h2>
          <div className="flex-1 overflow-y-auto px-6 pb-8">
            <ClinicDisplay
              coachUpcomingClinicLessonsData={mergedClinicData}
              loading={coachUpcomingClinicLessonsLoading}
              handleGetUpcomingLessonsById={handleGetUpcomingLessonsById}
              showBackground={false}
              showTitle={false}
            />
          </div>
        </div>
      </Modal>

      {isCoachOfPage && (
        <>
          <UploadProvider>
            <UploadContentModal
              open={uploadContentModal.isOpen}
              handleClose={uploadContentModal.closeModal}
              onSuccessUpload={handleGetCoachPremiumContent}
            />
          </UploadProvider>
          <SetPriceModal
            initialValues={priceFormInitialValues}
            open={setPriceModal.isOpen}
            handleClose={setPriceModal.closeModal}
            onSuccess={handleGetCoachPremiumContent}
            currency={
              coachData?.stripeMerchantCurrency || coachData?.currencyCoachDefault || CURRENCY_USD
            }
          />
        </>
      )}
      <Modal isOpen={isContactCoachModalOpen} handleClose={closeContactCoachModal}>
        <ContactCoach
          coachData={coachData}
          starterId={coachStarterProfile?.id}
          openAuthModal={openSignupModal}
          visible={true}
          onSuccess={handleContactCoachSuccess}
        />
      </Modal>
      <ContactCoachSuccessModal
        isOpen={isContactCoachSuccessModalOpen}
        handleClose={closeContactCoachSuccessModal}
      />
      {!!jsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      )}
    </>
  );
}
